import { useCurrentWorkflowStore } from '@/stores/workflow/currentWorkflow';
import { Api } from '@/api';
import { toast } from 'vue-sonner';
import { ref } from 'vue';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner';
import { useInjectedConditionEditor } from './useWorkflowConditionEditorProvider';
import { useConditionDisplay } from './useConditionDisplay';

export const useWorkflowCondition = () => {
  const workflowStore = useCurrentWorkflowStore();
  const loadingSpinnerStore = useLoadingSpinnerStore();
  const conditionEditor = useInjectedConditionEditor();
  const { conditionDisplay } = useConditionDisplay();

  const condition = conditionDisplay(conditionEditor.type);

  const showSaveChangesModal = ref(false);

  const createCondition = async () => {
    const response = await Api.WorkflowCondition.createWorkflowCondition({
      workflowId: workflowStore.currentWorkflow.id,
      expression: conditionEditor.localExpression.value,
      triggerDefinitionKey: workflowStore.currentWorkflow.trigger_definition_key,
      conditionType: conditionEditor.type,
    });
    if (response.status === 'success') {
      conditionEditor.syncPersistedExpression();
      const description = `Your workflow ${condition.lowercase} was successfully created.`;
      toast.success(`Workflow ${condition.capital} Created`, { description });
    } else {
      const description = response.messages.join(', ');
      toast.error(`Error Creating Workflow ${condition.capital}`, { description });
    }
  };

  const updateCondition = async () => {
    const response = await Api.WorkflowCondition.updateWorkflowCondition({
      workflowId: workflowStore.currentWorkflow.id,
      expression: conditionEditor.localExpression.value,
      conditionType: conditionEditor.type,
    });
    loadingSpinnerStore.hideLocalLoadingSpinner();

    if (response.status === 'success') {
      conditionEditor.syncPersistedExpression();
      const description = `Your workflow ${condition.lowercase} was successfully updated.`;
      toast.success(`Workflow ${condition.capital} Updated`, { description });
      showSaveChangesModal.value = false;
    } else {
      const description = response.messages.join(', ');
      toast.error(`Error Updating Workflow ${condition.capital}`, { description });
    }
  };

  const deleteCondition = async () => {
    const response = await Api.WorkflowCondition.deleteWorkflowCondition({
      workflowId: workflowStore.currentWorkflow.id,
      conditionType: conditionEditor.type,
    });
    loadingSpinnerStore.hideLocalLoadingSpinner();

    if (response.status === 'success') {
      conditionEditor.resetToNew();
      const description = `Your workflow ${condition.lowercase} was successfully deleted.`;
      toast.success(`Workflow ${condition.capital} Deleted`, { description });
      showSaveChangesModal.value = false;
    } else {
      const description = response.messages.join(', ');
      toast.error(`Error Updating Workflow ${condition.capital}`, { description });
    }
  };

  const displaySaveChangesModal = () => {
    showSaveChangesModal.value = true;
  };

  return { showSaveChangesModal, createCondition, updateCondition, deleteCondition, displaySaveChangesModal };
};
